import { useEffect, useState, useCallback } from "react";
import AdminLayout from "../layout/AdminLayout";
import { API_BASE_URL } from "../../config/api";

const token = () => localStorage.getItem("adminToken");

const fmt = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const inr = (n) =>
  n !== undefined && n !== null
    ? "₹" + Number(n).toLocaleString("en-IN")
    : "—";

export default function AdminInvoices() {
  const [invoices, setInvoices]   = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState("");
  const [resending, setResending] = useState(null);
  const [msg, setMsg]             = useState("");

  // Filters
  const [yearFilter, setYearFilter]       = useState("");
  const [monthFilter, setMonthFilter]     = useState("");
  const [gstFilter, setGstFilter]         = useState("");
  const [searchFilter, setSearchFilter]   = useState("");

  const LIMIT = 20;

  // Build financial year options (2025 → current FY)
  const currentFY = (() => {
    const now = new Date();
    return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  })();
  const fyOptions = [];
  for (let y = 2025; y <= currentFY; y++) fyOptions.push(String(y));

  const fetchInvoices = useCallback(async (p = 1) => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ page: p, limit: LIMIT });
      if (yearFilter)   params.append("year",    yearFilter);
      if (monthFilter)  params.append("month",   monthFilter);
      if (gstFilter)    params.append("gstType", gstFilter);
      if (searchFilter) params.append("search",  searchFilter);

      const res = await fetch(`${API_BASE_URL}/api/invoices?${params}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to load invoices");
      setInvoices(data.invoices || []);
      setTotal(data.total || 0);
      setPage(p);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [yearFilter, monthFilter, gstFilter, searchFilter]);

  useEffect(() => { fetchInvoices(1); }, [fetchInvoices]);

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (yearFilter)  params.append("year",  yearFilter);
      if (monthFilter) params.append("month", monthFilter);
      const res = await fetch(`${API_BASE_URL}/api/invoices/export?${params}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      const MONTH_NAMES = ["","Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      const ml = monthFilter ? `_${MONTH_NAMES[parseInt(monthFilter)]}` : "";
      const yl = yearFilter  ? `_FY${yearFilter}` : "_All";
      a.download = `FDS_Invoices${yl}${ml}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("Export failed: " + err.message);
    }
  };

  const handleResend = async (id, email) => {
    if (!window.confirm(`Resend invoice email to ${email}?`)) return;
    setResending(id);
    setMsg("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/invoices/${id}/resend`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token()}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setMsg(`✅ Invoice resent to ${email}`);
    } catch (err) {
      setMsg("❌ " + err.message);
    } finally {
      setResending(null);
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  // Summary totals from current page
  const summary = invoices.reduce(
    (acc, inv) => ({
      base:  acc.base  + (inv.baseAmount  || 0),
      gst:   acc.gst   + (inv.totalGst   || 0),
      total: acc.total + (inv.totalAmount || 0),
    }),
    { base: 0, gst: 0, total: 0 }
  );

  return (
    <AdminLayout>
      <div style={{ padding: "24px" }}>
        {/* ── Header ── */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
          <div>
            <h2 style={{ margin: 0, color: "#1b2559", fontSize: 22 }}>🧾 GST Invoices</h2>
            <p style={{ margin: "4px 0 0", color: "#6b7280", fontSize: 13 }}>
              Auto-generated tax invoices for subscription payments — Uvars OPC Private Limited (GSTIN: 37AACCU9967H1ZN)
            </p>
          </div>
          <button
            onClick={handleExport}
            style={{ background: "#16a34a", color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", cursor: "pointer", fontWeight: 600, fontSize: 13 }}
          >
            ⬇ Export Excel (GSTR-1)
          </button>
        </div>

        {/* ── Filters ── */}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
          <select
            value={yearFilter}
            onChange={e => setYearFilter(e.target.value)}
            style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }}
          >
            <option value="">All Financial Years</option>
            {fyOptions.map(y => <option key={y} value={y}>FY {y}–{String(parseInt(y) + 1).slice(2)}</option>)}
          </select>

          <select
            value={monthFilter}
            onChange={e => setMonthFilter(e.target.value)}
            style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }}
          >
            <option value="">All Months</option>
            {["January","February","March","April","May","June","July","August","September","October","November","December"]
              .map((m, i) => <option key={i+1} value={String(i+1)}>{m}</option>)}
          </select>

          <select
            value={gstFilter}
            onChange={e => setGstFilter(e.target.value)}
            style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }}
          >
            <option value="">All GST Types</option>
            <option value="CGST_SGST">CGST + SGST (Intrastate)</option>
            <option value="IGST">IGST (Interstate)</option>
          </select>

          <input
            type="text"
            placeholder="Search invoice, name, email, TxID…"
            value={searchFilter}
            onChange={e => setSearchFilter(e.target.value)}
            onKeyDown={e => e.key === "Enter" && fetchInvoices(1)}
            style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13, minWidth: 240 }}
          />
          <button
            onClick={() => fetchInvoices(1)}
            style={{ padding: "8px 16px", borderRadius: 8, background: "#4318FF", color: "#fff", border: "none", cursor: "pointer", fontSize: 13 }}
          >
            Search
          </button>
        </div>

        {msg && (
          <div style={{ marginBottom: 12, padding: "10px 14px", borderRadius: 8, background: msg.startsWith("✅") ? "#f0fdf4" : "#fef2f2", color: msg.startsWith("✅") ? "#15803d" : "#dc2626", fontSize: 13 }}>
            {msg}
          </div>
        )}

        {/* ── Summary cards ── */}
        {invoices.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 12, marginBottom: 20 }}>
            {[
              { label: "Records shown", value: invoices.length, color: "#4318FF" },
              { label: "Taxable Value", value: inr(summary.base), color: "#0369a1" },
              { label: "Total GST",     value: inr(summary.gst),  color: "#7c3aed" },
              { label: "Total Revenue", value: inr(summary.total), color: "#15803d" },
            ].map(c => (
              <div key={c.label} style={{ background: "#f4f7fe", borderRadius: 10, padding: "14px 16px" }}>
                <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.4px" }}>{c.label}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: c.color }}>{c.value}</div>
              </div>
            ))}
          </div>
        )}

        {error && <div style={{ color: "#dc2626", marginBottom: 12 }}>❌ {error}</div>}
        {loading && <div style={{ color: "#6b7280", marginBottom: 12 }}>Loading…</div>}

        {/* ── Table ── */}
        <div style={{ overflowX: "auto", borderRadius: 10, border: "1px solid #e8edf7" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f4f7fe" }}>
                {["Invoice No", "Date", "Buyer / Company", "State", "GST Type", "Taxable", "GST", "Total", "Tx ID", "Actions"].map(h => (
                  <th key={h} style={{ padding: "10px 12px", textAlign: "left", color: "#1b2559", fontWeight: 600, whiteSpace: "nowrap", borderBottom: "1px solid #e8edf7" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 && !loading && (
                <tr>
                  <td colSpan={10} style={{ textAlign: "center", padding: 40, color: "#6b7280" }}>
                    No invoices found.
                  </td>
                </tr>
              )}
              {invoices.map((inv, i) => (
                <tr key={inv._id} style={{ background: i % 2 === 0 ? "#fff" : "#fafbff", borderBottom: "1px solid #f1f5f9" }}>
                  <td style={{ padding: "10px 12px", fontWeight: 600, color: "#4318FF", whiteSpace: "nowrap" }}>{inv.invoiceNumber}</td>
                  <td style={{ padding: "10px 12px", whiteSpace: "nowrap", color: "#374151" }}>{fmt(inv.invoiceDate)}</td>
                  <td style={{ padding: "10px 12px" }}>
                    <div style={{ fontWeight: 600, color: "#1b2559" }}>{inv.buyer?.companyName || inv.buyer?.name}</div>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>{inv.buyer?.email}</div>
                  </td>
                  <td style={{ padding: "10px 12px", color: "#374151", whiteSpace: "nowrap" }}>
                    {inv.buyer?.state || "—"}
                    {inv.buyer?.stateCode ? ` (${inv.buyer.stateCode})` : ""}
                  </td>
                  <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                    <span style={{
                      background: inv.gstType === "CGST_SGST" ? "#dbeafe" : "#fce7f3",
                      color:      inv.gstType === "CGST_SGST" ? "#1d4ed8" : "#9d174d",
                      padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600,
                    }}>
                      {inv.gstType === "CGST_SGST" ? "CGST+SGST" : "IGST"}
                    </span>
                  </td>
                  <td style={{ padding: "10px 12px", whiteSpace: "nowrap", color: "#374151" }}>{inr(inv.baseAmount)}</td>
                  <td style={{ padding: "10px 12px", whiteSpace: "nowrap", color: "#7c3aed", fontWeight: 600 }}>{inr(inv.totalGst)}</td>
                  <td style={{ padding: "10px 12px", whiteSpace: "nowrap", fontWeight: 700, color: "#15803d" }}>{inr(inv.totalAmount)}</td>
                  <td style={{ padding: "10px 12px", fontSize: 11, color: "#6b7280", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={inv.transactionId}>
                    {inv.transactionId || "—"}
                  </td>
                  <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                    <button
                      onClick={() => handleResend(inv._id, inv.buyer?.email)}
                      disabled={resending === inv._id}
                      style={{ background: "#4318FF", color: "#fff", border: "none", borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 12 }}
                    >
                      {resending === inv._id ? "Sending…" : "📧 Resend"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 20, flexWrap: "wrap" }}>
            <button
              onClick={() => fetchInvoices(page - 1)}
              disabled={page <= 1}
              style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid #e2e8f0", cursor: page <= 1 ? "not-allowed" : "pointer", background: "#fff" }}
            >
              ← Prev
            </button>
            <span style={{ padding: "6px 14px", color: "#6b7280", fontSize: 13 }}>
              Page {page} of {totalPages} &nbsp;·&nbsp; {total} total
            </span>
            <button
              onClick={() => fetchInvoices(page + 1)}
              disabled={page >= totalPages}
              style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid #e2e8f0", cursor: page >= totalPages ? "not-allowed" : "pointer", background: "#fff" }}
            >
              Next →
            </button>
          </div>
        )}

        {/* ── GST Info footer ── */}
        <div style={{ marginTop: 24, padding: "14px 16px", background: "#f0f4ff", borderRadius: 8, fontSize: 12, color: "#6b7280", lineHeight: 1.8 }}>
          <strong style={{ color: "#1b2559" }}>Seller Details:</strong> Uvars OPC Private Limited &nbsp;·&nbsp;
          GSTIN: 37AACCU9967H1ZN &nbsp;·&nbsp; CIN: U72900AP2021OPC120392 &nbsp;·&nbsp; SAC: 998319 &nbsp;·&nbsp;
          State: Andhra Pradesh (37) &nbsp;·&nbsp; GST Rate: 18%<br/>
          <strong style={{ color: "#1b2559" }}>CGST+SGST</strong> applies when buyer state = Andhra Pradesh &nbsp;·&nbsp;
          <strong style={{ color: "#1b2559" }}>IGST</strong> applies for all other states
        </div>
      </div>
    </AdminLayout>
  );
}
